// The calculator's brain, with no React in it so it can be tested on its own.
//
// An expression is kept as plain ASCII text - digits . + - * / ( ) % - and only dressed up
// (× ÷ −, thousands separators) when it is shown (prettyExpr).
//
// The percent key reads the way a shop calculator does:
//   200 + 10%   is 200 + (10% OF 200) = 220      (so does −)
//   50% × 200   is 0.5 × 200 = 100
//   50%         is 0.5
// There is no eval anywhere: the text is tokenised and parsed by hand.

const OPS = '+-*/';
const HISTORY_MAX = 30;
const isDigit = (c) => c >= '0' && c <= '9';
const isOp = (c) => c !== '' && OPS.includes(c);
const last = (s) => s.slice(-1);

class CalcError extends Error {
  constructor(code) { super(code); this.code = code; }
}

function tokenize(expr) {
  const out = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (isDigit(c) || c === '.') {
      let j = i;
      let dots = 0;
      while (j < expr.length && (isDigit(expr[j]) || expr[j] === '.')) { if (expr[j] === '.') dots += 1; j += 1; }
      const text = expr.slice(i, j);
      if (dots > 1 || text === '.') throw new CalcError('syntax');
      out.push({ t: 'num', v: parseFloat(text) });
      i = j;
    } else if (isOp(c) || c === '(' || c === ')' || c === '%') {
      out.push({ t: c });
      i += 1;
    } else {
      throw new CalcError('syntax');
    }
  }
  return out;
}

function parse(tokens) {
  let p = 0;
  const peek = () => tokens[p];
  const next = () => { const tk = tokens[p]; p += 1; return tk; };

  function primary() {
    const tk = next();
    if (!tk) throw new CalcError('syntax');
    if (tk.t === 'num') return { k: 'num', v: tk.v };
    if (tk.t === '(') {
      const inner = expression();
      const close = next();
      if (!close || close.t !== ')') throw new CalcError('syntax');
      return inner;
    }
    throw new CalcError('syntax');
  }
  function postfix() {
    let a = primary();
    while (peek() && peek().t === '%') { next(); a = { k: 'pct', a }; }
    return a;
  }
  function unary() {
    if (peek() && peek().t === '-') { next(); return { k: 'neg', a: unary() }; }
    if (peek() && peek().t === '+') { next(); return unary(); }
    return postfix();
  }
  function term() {
    let left = unary();
    while (peek() && (peek().t === '*' || peek().t === '/')) {
      const op = next().t;
      left = { k: 'bin', op, left, right: unary() };
    }
    return left;
  }
  function expression() {
    let left = term();
    while (peek() && (peek().t === '+' || peek().t === '-')) {
      const op = next().t;
      left = { k: 'bin', op, left, right: term() };
    }
    return left;
  }

  const tree = expression();
  if (p !== tokens.length) throw new CalcError('syntax');
  return tree;
}

function run(node) {
  switch (node.k) {
    case 'num': return node.v;
    case 'neg': return -run(node.a);
    case 'pct': return run(node.a) / 100;
    default: {
      const l = run(node.left);
      if (node.op === '+' || node.op === '-') {
        // "200 + 10%" means 10% OF 200
        const r = node.right.k === 'pct' ? l * run(node.right) : run(node.right);
        return node.op === '+' ? l + r : l - r;
      }
      const r = run(node.right);
      if (node.op === '*') return l * r;
      if (r === 0) throw new CalcError('div0');
      return l / r;
    }
  }
}

/** { ok: true, value } or { ok: false, error: 'syntax' | 'div0' | 'overflow' } */
export function evaluate(expr) {
  try {
    const value = run(parse(tokenize(expr)));
    if (!Number.isFinite(value) || Math.abs(value) >= 1e15) return { ok: false, error: 'overflow' };
    // 0.1 + 0.2 must read 0.3, not 0.30000000000000004
    return { ok: true, value: Number(value.toPrecision(12)) };
  } catch (err) {
    return { ok: false, error: err instanceof CalcError ? err.code : 'syntax' };
  }
}

/** The number as plain decimal text (no exponent, no thousands separators): what the next calculation continues from. */
export function plain(value) {
  if (value === 0) return '0';
  // The same 12 significant digits evaluate() keeps, so carrying on from an answer (1/3 = then × 3) loses nothing.
  let s = Math.abs(value) < 1e-6 ? value.toFixed(12) : value.toPrecision(12);
  if (/e/i.test(s)) s = value.toFixed(0);                                   // 1e12 and up come back in exponent form
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s === '-0' ? '0' : s;
}

/** '1234567.5' -> '1,234,567.5'; '12.' stays '12.' (the dot the person just typed). */
export function groupDigits(text) {
  const m = /^(-?)(\d+)(\.\d*)?$/.exec(text);
  if (!m) return text;
  return m[1] + m[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (m[3] || '');
}

/** The expression as shown: grouped numbers, × ÷ − glyphs, a space around a binary operator. */
export function prettyExpr(expr) {
  let out = '';
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (isDigit(c) || c === '.') {
      let j = i;
      while (j < expr.length && (isDigit(expr[j]) || expr[j] === '.')) j += 1;
      out += groupDigits(expr.slice(i, j));
      i = j;
    } else {
      const before = expr[i - 1] || '';
      const binary = isOp(c) && (isDigit(before) || before === ')' || before === '%' || before === '.');
      const glyph = c === '*' ? '×' : c === '/' ? '÷' : c === '-' ? '−' : c;
      out += binary ? ` ${glyph} ` : glyph;
      i += 1;
    }
  }
  return out;
}

/** Trailing operators dropped and open brackets closed, so a half-typed sum can still be answered. */
export function tidyExpr(expr) {
  let e = expr;
  while (e && (isOp(last(e)) || last(e) === '(')) e = e.slice(0, -1);
  const open = (e.match(/\(/g) || []).length - (e.match(/\)/g) || []).length;
  return e + ')'.repeat(Math.max(0, open));
}

// Is there anything to work out, or is it just a number (maybe a negative one)?
const hasOperation = (e) => /[-+*/%(]/.test(e.replace(/^-/, ''));

/** What the sum comes to so far, or null when there is nothing to show yet. */
export function preview(expr) {
  const tidy = tidyExpr(expr);
  if (!tidy || !hasOperation(tidy)) return null;
  const r = evaluate(tidy);
  return r.ok ? plain(r.value) : null;
}

export const INITIAL = { expr: '', prev: '', evaluated: false, error: '', history: [], memory: null };

const currentNumber = (s) => (s.match(/[0-9.]*$/) || [''])[0];
const openParens = (s) => (s.match(/\(/g) || []).length - (s.match(/\)/g) || []).length;

function equals(state) {
  const tidy = tidyExpr(state.expr);
  if (!tidy || !hasOperation(tidy)) return { ...state, error: '' };          // a bare number: nothing to work out
  const r = evaluate(tidy);
  if (!r.ok) return { ...state, error: r.error };
  const result = plain(r.value);
  const entry = { expr: tidy, result };
  return { ...state, expr: result, prev: tidy, evaluated: true, error: '', history: [entry, ...state.history].slice(0, HISTORY_MAX) };
}

// ── the number being typed ──────────────────────────────────────────────────────────────────────────────────────────
// { start, text, value } of the number at the END of the expression; start includes a unary minus in front of it.
function currentEntry(expr) {
  const m = /[0-9.]+$/.exec(expr);
  if (!m) return null;
  let start = m.index;
  const before = expr.slice(0, start);
  if (before.endsWith('-') && (before.length === 1 || /[-+*/(]$/.test(before.slice(0, -1)))) start -= 1;
  const text = expr.slice(start);
  const value = Number(text);
  return Number.isFinite(value) ? { start, text, value } : null;
}

// ── CE, ±, √, x², 1/x ───────────────────────────────────────────────────────────────────────────────────────────────
const UNARY = {
  neg: (v) => -v,
  sqrt: (v) => { if (v < 0) throw new CalcError('invalid'); return Math.sqrt(v); },
  sqr: (v) => v * v,
  recip: (v) => { if (v === 0) throw new CalcError('div0'); return 1 / v; },
};

// Applies to the answer just shown, or to the number being typed (and replaces it with the result, as the display does).
function applyUnary(state, op) {
  const base = { ...state, error: '' };
  let source;
  let start = 0;
  if (state.evaluated) {
    source = Number(state.expr);
  } else {
    const entry = currentEntry(state.expr);
    if (!entry) return base;
    source = entry.value;
    start = entry.start;
  }
  if (op === 'neg' && source === 0) return base;                              // negating 0 changes nothing
  let value;
  try { value = UNARY[op](source); } catch (err) { return { ...state, error: err instanceof CalcError ? err.code : 'syntax' }; }
  if (!Number.isFinite(value) || Math.abs(value) >= 1e15) return { ...state, error: 'overflow' };
  const text = plain(Number(value.toPrecision(12)));
  if (state.evaluated) return { ...base, expr: text, prev: '', evaluated: true };
  return { ...base, expr: state.expr.slice(0, start) + text };
}

// CE: clears the number being typed and leaves the rest of the sum; after an answer or an error it clears everything.
function clearEntry(state) {
  if (state.evaluated || state.error) return { ...INITIAL, history: state.history, memory: state.memory };
  const entry = currentEntry(state.expr);
  if (!entry) return { ...state, error: '' };
  return { ...state, error: '', expr: state.expr.slice(0, entry.start) };
}

// ── memory ──────────────────────────────────────────────────────────────────────────────────────────────────────────
// What the display shows right now, as a number (null while it shows an error).
export function displayValue(state) {
  if (state.error) return null;
  if (state.evaluated) { const v = Number(state.expr); return Number.isFinite(v) ? v : null; }
  const tidy = tidyExpr(state.expr);
  if (!tidy) return 0;
  const r = evaluate(tidy);
  return r.ok ? r.value : null;
}

/** The display as plain digits (no separators) - what Copy takes. */
export function resultText(state) {
  const v = displayValue(state);
  return v === null ? '' : plain(v);
}

// A whole number put in as the entry: MR. After an answer it is a fresh entry (a digit starts a new one, an operator carries on).
function enterNumber(state, text) {
  if (state.evaluated) return { ...state, error: '', expr: text, prev: '', evaluated: true };
  const entry = currentEntry(state.expr);
  if (entry) return { ...state, error: '', expr: state.expr.slice(0, entry.start) + text };
  const end = last(state.expr);
  return { ...state, error: '', expr: state.expr + (end === ')' || end === '%' ? '*' : '') + text };
}

function memoryKey(state, k) {
  const base = { ...state, error: '' };
  if (k === 'mc') return { ...base, memory: null };
  if (k === 'mr') return state.memory === null || state.memory === undefined ? base : enterNumber(state, plain(state.memory));
  const v = displayValue(state);
  if (v === null) return base;
  if (k === 'ms') {
    const stored = Number(v.toPrecision(12));
    if (!Number.isFinite(stored) || Math.abs(stored) >= 1e15) return { ...state, error: 'overflow' };   // 999999999999999 rounds up to 1e15
    return { ...base, memory: stored };
  }
  const held = state.memory === null || state.memory === undefined ? 0 : state.memory;
  const next = Number((k === 'mplus' ? held + v : held - v).toPrecision(12));
  if (!Number.isFinite(next) || Math.abs(next) >= 1e15) return { ...state, error: 'overflow' };
  return { ...base, memory: next };
}

/**
 * One key press. keys: '0'-'9' '.' '+' '-' '*' '/' '(' ')' '%' '=' · 'back' (Backspace) · 'ce' (clear entry) · 'clear' (C, all)
 * · 'neg' (±) 'sqrt' 'sqr' (x²) 'recip' (1/x) · 'ms' 'mplus' 'mminus' 'mr' 'mc' (memory).
 * Pure: returns the next state, never mutates the old one.
 */
export function press(state, key) {
  const base = { ...state, error: '' };
  if (key === 'clear') return { ...INITIAL, history: state.history, memory: state.memory };
  if (key === 'ce') return clearEntry(state);
  if (key === 'neg' || key === 'sqrt' || key === 'sqr' || key === 'recip') return applyUnary(state, key);
  if (key === 'ms' || key === 'mplus' || key === 'mminus' || key === 'mr' || key === 'mc') return memoryKey(state, key);
  if (key === 'back') {
    if (state.evaluated) return { ...base, expr: state.expr.slice(0, -1), prev: '', evaluated: false };
    return { ...base, expr: state.expr.slice(0, -1) };
  }
  if (key === '=') return equals(state);

  // After "=": an operator carries on from the answer, anything else starts a new sum.
  if (state.evaluated) {
    if (isOp(key) || key === '%') return press({ ...base, evaluated: false, prev: '' }, key);
    return press({ ...base, expr: '', evaluated: false, prev: '' }, key);
  }

  let { expr } = state;
  const end = last(expr);

  if (isDigit(key)) {
    if (end === ')' || end === '%') expr += '*';
    else if (currentNumber(expr) === '0') expr = expr.slice(0, -1);          // 0 then 5 reads 5, not 05
    return { ...base, expr: expr + key };
  }
  if (key === '.') {
    if (end === ')' || end === '%') expr += '*';
    const cur = currentNumber(expr);
    if (cur.includes('.')) return base;
    return { ...base, expr: expr + (cur === '' ? '0.' : '.') };
  }
  if (isOp(key)) {
    if (expr === '' || end === '(') return key === '-' ? { ...base, expr: expr + '-' } : base;
    if (isOp(end)) {
      if (key === '-' && (end === '*' || end === '/')) return { ...base, expr: expr + '-' };   // 5 × −3
      let e = expr;
      while (isOp(last(e))) e = e.slice(0, -1);
      if (e === '' || last(e) === '(') return key === '-' ? { ...base, expr: e + '-' } : { ...base, expr: e };
      return { ...base, expr: e + key };
    }
    return { ...base, expr: expr + key };
  }
  if (key === '%') {
    if (isDigit(end) || end === ')' || end === '%') return { ...base, expr: expr + '%' };
    return base;
  }
  if (key === '(') {
    if (isDigit(end) || end === ')' || end === '%' || end === '.') expr += '*';
    return { ...base, expr: expr + '(' };
  }
  if (key === ')') {
    if (openParens(expr) > 0 && (isDigit(end) || end === ')' || end === '%')) return { ...base, expr: expr + ')' };
    return base;
  }
  return base;
}

// ── the keyboard, the way the Windows Calculator has it ─────────────────────────────────────────────────────────────
//   digits (top row and numpad)  0-9           . decimal point        + − * /          Enter or =  equals
//   Backspace  delete the last digit           Delete  CE (clear entry)                 Esc  C (clear all)
//   F9  ± (negate)    R  1/x    @  square root    %  percent
//   Ctrl+M store · Ctrl+P add · Ctrl+Q subtract · Ctrl+R recall · Ctrl+L clear memory
//   Ctrl+H  show / hide history     Ctrl+Shift+D  clear history     Ctrl+C  copy     Ctrl+V  paste (see pasteKeys)
// Also here, because the engine does brackets: ( and ). Persian and Arabic-Indic digits count as digits. A comma is
// ignored, so "1,500+2,500" can be typed the way it is written.
//
// event -> { press: key } (a key for press()) | { ui: 'history' | 'clearHistory' | 'copy' } | { swallow: true } (a printable
// character that means nothing here: it must not go into a field underneath) | null (not ours: Tab, arrows, F5, Alt+..., Ctrl+A ...)
export function keyAction(e) {
  const { key } = e;
  if (!key || e.altKey || e.metaKey) return null;
  if (e.ctrlKey) {
    const k = key.length === 1 ? key.toLowerCase() : key;
    if (e.shiftKey) return k === 'd' ? { ui: 'clearHistory' } : null;
    switch (k) {
      case 'm': return { press: 'ms' };
      case 'p': return { press: 'mplus' };
      case 'q': return { press: 'mminus' };
      case 'r': return { press: 'mr' };
      case 'l': return { press: 'mc' };
      case 'h': return { ui: 'history' };
      case 'c': return { ui: 'copy' };
      default: return null;                                                  // Ctrl+V is the paste event's, Ctrl+A / Ctrl+Z / ... stay the browser's
    }
  }
  if (key === 'Enter' || key === '=') return { press: '=' };
  if (key === 'Backspace') return { press: 'back' };
  if (key === 'Delete') return { press: 'ce' };
  if (key === 'Escape') return { press: 'clear' };
  if (key === 'F9') return { press: 'neg' };
  if (key.length !== 1) return null;
  if (isDigit(key)) return { press: key };
  const code = key.charCodeAt(0);
  if (code >= 0x06f0 && code <= 0x06f9) return { press: String(code - 0x06f0) };   // ۰-۹
  if (code >= 0x0660 && code <= 0x0669) return { press: String(code - 0x0660) };   // ٠-٩
  if (key === '.' || key === '٫') return { press: '.' };
  if (key === '+') return { press: '+' };
  if (key === '-' || key === '−') return { press: '-' };
  if (key === '*' || key === '×') return { press: '*' };
  if (key === '/' || key === '÷') return { press: '/' };
  if (key === '(' || key === ')' || key === '%') return { press: key };
  if (key === '@') return { press: 'sqrt' };
  if (key === 'r' || key === 'R') return { press: 'recip' };
  return { swallow: true };
}

/** Pasted text as a list of keys for press(), or null when it is not a number or a sum (it is then left alone). */
export function pasteKeys(text) {
  if (typeof text !== 'string') return null;
  const cleaned = text
    .replace(/[\s,٬]/g, '')                                                           // spaces and thousands separators
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/٫/g, '.').replace(/−/g, '-').replace(/×/g, '*').replace(/÷/g, '/');
  if (!cleaned || cleaned.length > 200 || !/^[0-9.+\-*/()%]+$/.test(cleaned)) return null;
  return [...cleaned];
}
